import {
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useDebounceCallback,
} from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import type { FilterConditionDTO } from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { CategoryTreePopover } from "@/modules/catalog/presentation/ui/CategoryTreePopover";
import { useCategoriesQuery } from "@/modules/categories/presentation/api/queries";
import { feesApi } from "../api/client";

const SCOPE_OPTIONS = [
  { value: "all", label: "ທຸກຂອບເຂດ" },
  { value: "global", label: "ທົ່ວແຜງ" },
  { value: "shop", label: "ຮ້ານຄ້າ" },
  { value: "category", label: "ຫມວດຫມູ່" },
];

const STATUS_OPTIONS = [
  { value: "all", label: "ທຸກສະຖານະ" },
  { value: "active", label: "ໃຊ້ງານ" },
  { value: "inactive", label: "ປິດໃຊ້ງານ" },
];

type FeesSearch = {
  limit?: number;
  offset?: number;
  sort?: Array<{ field: string; dir: "asc" | "desc" }>;
  filters?: FilterConditionDTO[];
  q?: string;
};

export function FeesFilter() {
  const nav = useNavigate({ from: "/app/fees" });
  const search = useSearch({ from: "/app/fees" }) as FeesSearch;

  const filters = search.filters ?? [];
  const scopeFilter = findCondition(filters, "scope");
  const scopeIdFilter = findCondition(filters, "scopeId");
  const isActiveFilter = findCondition(filters, "isActive");

  const [searchValue, setSearchValue] = useState<string>(search.q ?? "");
  const scopeValue = (scopeFilter?.value as string) ?? "all";
  const scopeIdValue = (scopeIdFilter?.value as string) ?? "all";
  const statusValue = isActiveFilter
    ? (isActiveFilter.value as boolean) === true
      ? "active"
      : "inactive"
    : "all";

  useEffect(() => {
    setSearchValue(search.q ?? "");
  }, [search.q]);

  const debounced = useDebounceCallback((val: string) => {
    nav({
      search: {
        ...search,
        offset: 0,
        q: val.trim() || undefined,
      },
    });
  }, 400);

  const categoriesQuery = useCategoriesQuery({ limit: 1000, offset: 0 });
  const categoryItems = (categoriesQuery.data?.data ?? []).map((c) => ({
    id: c.id,
    name: c.name,
    parentId: c.parentId,
  }));
  const shopsQuery = useQuery({
    queryKey: ["fees", "lookup", "shops", "filter"],
    queryFn: () => feesApi.lookupShops({ limit: 200 }),
    enabled: scopeValue === "shop",
  });
  const shops = shopsQuery.data?.items ?? [];

  const applyScope = (value: string) => {
    let next = removeConditions(filters, "scope");
    next = removeConditions(next, "scopeId") ?? [];
    if (value !== "all") {
      next = upsertCondition(next, {
        field: "scope",
        op: "eq",
        value,
      });
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: next?.length ? next : undefined,
      },
    });
  };

  const applyScopeId = (value: string) => {
    let next = removeConditions(filters, "scopeId");
    if (value !== "all") {
      next = upsertCondition(next ?? [], {
        field: "scopeId",
        op: "eq",
        value,
      });
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: next?.length ? next : undefined,
      },
    });
  };

  const applyStatus = (value: string) => {
    let next = removeConditions(filters, "isActive");
    if (value === "active") {
      next = upsertCondition(next, {
        field: "isActive",
        op: "eq",
        value: true,
      });
    } else if (value === "inactive") {
      next = upsertCondition(next, {
        field: "isActive",
        op: "eq",
        value: false,
      });
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: next?.length ? next : undefined,
      },
    });
  };

  const hasFilter =
    searchValue.trim() ||
    scopeValue !== "all" ||
    scopeIdValue !== "all" ||
    statusValue !== "all";

  return (
    <div className="mb-4 flex items-center justify-between gap-4 px-2">
      <div className="flex flex-1 flex-wrap items-center gap-2">
        <Input
          placeholder="ຄົ້ນຫາກົດຕາມຊື່ຫຼືລະຫັດ..."
          value={searchValue}
          onChange={(e) => {
            const v = e.target.value;
            setSearchValue(v);
            debounced(v);
          }}
          className="h-8 sm:max-w-2xs"
        />
        <Select value={scopeValue} onValueChange={applyScope}>
          <SelectTrigger className="h-8 w-[130px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SCOPE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {scopeValue === "category" && (
          <div className="w-64 max-w-full">
            <CategoryTreePopover
              categories={categoryItems}
              value={scopeIdValue === "all" ? "" : scopeIdValue}
              onChange={(val) => applyScopeId(val || "all")}
              isLoading={categoriesQuery.isLoading}
              clearable
            />
          </div>
        )}
        {scopeValue === "shop" && (
          <Select
            value={scopeIdValue}
            onValueChange={applyScopeId}
            disabled={shopsQuery.isLoading}
          >
            <SelectTrigger className="h-8 w-[180px]">
              <SelectValue placeholder="ທຸກຮ້ານ" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">ທຸກຮ້ານ</SelectItem>
              {shops.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select value={statusValue} onValueChange={applyStatus}>
          <SelectTrigger className="h-8 w-[120px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              nav({
                search: {
                  ...search,
                  offset: 0,
                  filters: undefined,
                  q: undefined,
                },
              })
            }
          >
            ລ້າງຕົວກອງ
          </Button>
        )}
      </div>
    </div>
  );
}
